import './ExportSubscriptionsStatsModal.css';

import { Button, Divider, Flex, Form, ModalProps, Select, Tooltip } from 'antd';
import { Modal } from 'antd/lib';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormContainer } from '@leedpulse-monorepo/components';
import {
  EProductType,
  PartnersType,
  partnersTypeAsString,
  productTypeAsString,
  SubscriptionStatus,
  subscriptionStatusAsString,
  UserType,
  userTypeAsString,
  ESubscriptionActivity,
  subscriptionActivityAsString
} from '@leedpulse-monorepo/enums';
import {
  exportSubscriptionsStats,
  ExportSubscriptionsStatsResult,
} from '../../../domain/use_cases/subscriptions/exportSubscriptionsStats';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { getNextExportStatsDate } from '../../../domain/use_cases/subscriptions/getLastExportStatsDate';
import { HourglassOutlined } from '@ant-design/icons';
import PlansContext from '../../contexts/data/PlansContext';

export type TPartnerSystemFilter = PartnersType | 'backoffice_paiement_link' | '';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ExportSubscriptionsStatsModalProps extends ModalProps { }

const ExportSubscriptionsStatsModal: React.FC<
  ExportSubscriptionsStatsModalProps
> = ({ open, onCancel, ...props }) => {
  const authContext = useContext(AuthenticationContext);
  const plansContext = useContext(PlansContext);

  const [exportResult, setExportResult] =
    useState<ExportSubscriptionsStatsResult | null>(null);
  const [nextExportDate, setNextExportDate] = useState<Date | null>(null);
  const [nextExportCounter, setNextExportCounter] = useState<number>(0);

  const [subscriptionsStatus, setSubscriptionsStatus] = useState<
    SubscriptionStatus[]
  >([SubscriptionStatus.READY]);
  const [partnerSystems, setPartnerSystems] = useState<TPartnerSystemFilter[]>(['']);
  const [userTypes, setUsersType] = useState<UserType[]>([UserType.ADMIN]);
  const [planIds, setPlanIds] = useState<string[]>([]);
  const [activities, setActivities] = useState<ESubscriptionActivity[]>([]);
  const [isCheckingNextExportDate, setIsCheckingNextExportDate] =
    useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const isFormValid = useMemo(() => {
    return true; // We allow empty array filters now
  }, [subscriptionsStatus, partnerSystems, userTypes]);

  const plansOptions = useMemo(() => {
    const lpOptions = [];
    const lfOptions = [];
    const naOptions = [];

    for (const plan of plansContext.items) {
      const maxNameLength = 30;
      const planOption = <Select.Option key={plan.id} value={plan.id}>
        <Tooltip title={plan.name}>
          {plan.name.substring(0, maxNameLength)}{plan.name.length > maxNameLength ? '...' : ''} ({plan.id})
        </Tooltip>
      </Select.Option>;
      if (plan.product === EProductType.LEEDPULSE) {
        lpOptions.push(planOption);
      } else if (plan.product === EProductType.LEEDFLOW) {
        lfOptions.push(planOption);
      } else {
        naOptions.push(planOption);
      }
    }

    return [
      lpOptions.length > 0 ? <Select.OptGroup key={EProductType.LEEDPULSE} label={productTypeAsString(EProductType.LEEDPULSE)}>
        {lpOptions}
      </Select.OptGroup> : null,
      lfOptions.length > 0 ? <Select.OptGroup key={EProductType.LEEDFLOW} label={productTypeAsString(EProductType.LEEDFLOW)}>
        {lfOptions}
      </Select.OptGroup> : null,
      naOptions.length > 0 ? <Select.OptGroup key="NA" label="NA">
        {naOptions}
      </Select.OptGroup> : null,
    ].filter(Boolean);
  }, [plansContext.items]);

  const fetchNextExport = async () => {
    setIsCheckingNextExportDate(true);
    if (!authContext.authentication) {
      setIsCheckingNextExportDate(false);
      return;
    }

    getNextExportStatsDate(authContext.authentication)
      .then((response) => {
        if (response.nextAvailableExport) {
          const nextExportDate = new Date(response.nextAvailableExport);
          setNextExportDate(nextExportDate);
          setNextExportCounter(
            Math.ceil((nextExportDate.getTime() - Date.now()) / 1000)
          );
        } else {
          setNextExportDate(null);
          setNextExportCounter(0);
        }
      })
      .catch((e) => {
        setNextExportDate(null);
      })
      .finally(() => {
        setIsCheckingNextExportDate(false);
      });
  };

  const handleExportStats = useCallback(() => {
    setIsExporting(true);
    if (!authContext.authentication) {
      setIsExporting(false);
      return;
    }

    exportSubscriptionsStats(
      subscriptionsStatus,
      partnerSystems,
      userTypes,
      planIds,
      activities,
      authContext.authentication
    )
      .then(async (result) => {
        setExportResult(result);
        await fetchNextExport();
        setIsExporting(false);
      })
      .catch((error) => {
        console.error("Erreur lors de l'export des stats :", error);
      })
      .finally(() => {
        setIsExporting(false);
      });
  }, [subscriptionsStatus, partnerSystems, userTypes, planIds, activities, authContext.authentication]);


  useEffect(() => {
    // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
    // chargement declenche ici. Architecture Context + use case du repo.
    // eslint-disable-next-line react/set-state-in-effect
    fetchNextExport();
  }, []);

  useEffect(() => {
    if (nextExportCounter > 0) {
      const interval = setInterval(() => {
        setNextExportCounter(nextExportCounter - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [nextExportCounter]);

  return (
    <Modal
      {...props}
      open={open}
      onCancel={onCancel}
      title="Exporter des stats"
      width={'50rem'}
      footer={[
        nextExportCounter > 0 && nextExportDate ? (<span key="next-export-date" className="next-export-date">Prochaine exportation possible le {nextExportDate.toLocaleString()}</span>) : null,
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleExportStats}
          disabled={
            isExporting ||
            !isFormValid ||
            isCheckingNextExportDate ||
            nextExportCounter > 0
          }
        >
          {isExporting
            ? 'Exportation en cours...'
            : isCheckingNextExportDate
              ? "Vérification de la date de l'export..."
              : nextExportCounter > 0
                ? <Flex align="center"><HourglassOutlined style={{ marginRight: 6 }} />{nextExportCounter} secondes</Flex>
                : 'Exporter'}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <FormContainer>
          <Form.Item label="Statut de l'abonnement">
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={subscriptionsStatus}
              onChange={(value) => setSubscriptionsStatus(value)}
            >
              {Object.values(SubscriptionStatus).map((status) => (
                <Select.Option key={status} value={status}>
                  {subscriptionStatusAsString(status)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Partenaire">
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={partnerSystems}
              onChange={(value) => setPartnerSystems(value)}
            >
              <Select.Option key="none" value="">
                Aucun
              </Select.Option>
              <Select.Option key="backoffice_paiement_link" value="backoffice_paiement_link">
                Lien de paiement (backoffice)
              </Select.Option>
              {Object.values(PartnersType).map((partner) => (
                <Select.Option key={partner} value={partner}>
                  {partnersTypeAsString(partner)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Type d'utilisateur">
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              value={userTypes}
              onChange={(value) => setUsersType(value)}
            >
              {Object.values(UserType).map((userType) => (
                <Select.Option key={userType} value={userType}>
                  {userTypeAsString(userType)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </FormContainer>

        <FormContainer style={{ gridTemplateColumns: '2fr 1fr' }}>
          <Form.Item label="Plan">
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              maxTagCount={"responsive"}
              value={planIds}
              onChange={(value) => setPlanIds(value)}
            >
              {plansOptions}
            </Select>
          </Form.Item>

          <Form.Item label="Activité">
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              maxTagCount={"responsive"}
              value={activities}
              onChange={(value) => setActivities(value)}
            >
              {
                Object.values(ESubscriptionActivity).map((activity) => (
                  <Select.Option key={activity} value={activity}>
                    {subscriptionActivityAsString(activity)}
                  </Select.Option>
                ))
              }
            </Select>
          </Form.Item>
        </FormContainer>

      </Form>
      {exportResult && (
        <Flex vertical>
          <Divider />
          <span>
            {`Télécharger le fichier : `}
            <a href={exportResult.url} download={exportResult.filename}>
              {exportResult.filename}
            </a>
          </span>
          <Divider />
        </Flex>
      )}
    </Modal>
  );
};

export default ExportSubscriptionsStatsModal;
